import { X, ChevronLeft, ChevronRight } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { cn } from 'src/shared/utils/cn'

interface ImageGalleryModalProps {
  open: boolean
  onClose: () => void
  images: string[]
  initialImageIndex: number
}

const FADE_DURATION_MS = 300

const ImageGalleryModal = ({ images, initialImageIndex, onClose, open }: ImageGalleryModalProps) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(initialImageIndex)
  const [isVisible, setIsVisible] = useState(false)
  const [shouldRender, setShouldRender] = useState(open)
  const rafRef = useRef<number | null>(null)
  const hideTimeoutRef = useRef<number | null>(null)

  const clearTimers = () => {
    if (rafRef.current !== null) {
      window.cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
    if (hideTimeoutRef.current !== null) {
      window.clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
  }

  useEffect(() => {
    setCurrentImageIndex(initialImageIndex)
  }, [initialImageIndex])

  useEffect(() => {
    clearTimers()

    if (open) {
      setShouldRender(true)
      setIsVisible(false)
      rafRef.current = window.requestAnimationFrame(() => {
        rafRef.current = window.requestAnimationFrame(() => {
          setIsVisible(true)
        })
      })
      return () => {
        clearTimers()
      }
    }

    setIsVisible(false)
    hideTimeoutRef.current = window.setTimeout(() => {
      setShouldRender(false)
    }, FADE_DURATION_MS)

    return () => {
      clearTimers()
    }
  }, [open])

  const closeGallery = useCallback(() => {
    onClose()
  }, [onClose])

  const nextImage = useCallback(() => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length)
  }, [images])

  const prevImage = useCallback(() => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length)
  }, [images])

  // Handle keyboard events
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!open) return

      switch (event.key) {
        case 'Escape':
          closeGallery()
          break
        case 'ArrowLeft':
          prevImage()
          break
        case 'ArrowRight':
          nextImage()
          break
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, nextImage, prevImage, closeGallery])

  if (!shouldRender) return null

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex items-center justify-center bg-black/90 transition-opacity ease-out',
        isVisible ? 'opacity-100' : 'opacity-0',
      )}
      style={{ transitionDuration: `${FADE_DURATION_MS}ms` }}
    >
      <div className="relative size-full">
        {/* Close button */}
        <button
          onClick={closeGallery}
          className="absolute right-4 top-4 z-10 rounded-full bg-black/50 p-2 text-white backdrop-blur-sm hover:bg-black/70"
        >
          <X size={24} />
        </button>

        {/* Navigation buttons */}
        {images.length > 1 && (
          <>
            <button
              onClick={prevImage}
              className="absolute left-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white backdrop-blur-sm hover:bg-black/70"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={nextImage}
              className="absolute right-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white backdrop-blur-sm hover:bg-black/70"
            >
              <ChevronRight size={24} />
            </button>
          </>
        )}

        {/* Image counter */}
        <div className="absolute left-4 top-4 z-10 rounded-lg bg-black/50 px-3 py-1 text-sm font-medium text-white backdrop-blur-sm">
          {currentImageIndex + 1} of {images.length}
        </div>

        {/* Main image */}
        <div className="flex h-full max-h-[90vh] items-center justify-center p-4">
          <img src={images[currentImageIndex]} alt="" className="max-h-full max-w-full rounded-lg object-contain" />
        </div>

        {/* Thumbnail strip */}
        {images.length > 1 && (
          <div className="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 gap-2">
            {images.map((image, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`size-16 overflow-hidden rounded-lg border-2 ${
                  index === currentImageIndex ? 'border-white' : 'border-white/30'
                }`}
              >
                <img src={image} alt="" className="size-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default ImageGalleryModal
